import React, { useEffect } from 'react';
import {
  FaArrowRight, FaBolt, FaCode, FaMapMarkedAlt, FaMobileAlt,
  FaMoneyBillWave, FaMotorcycle, FaPuzzlePiece,
} from 'react-icons/fa';
import Integrations from '../components/Integrations';
import AIOptimization from '../components/AIOptimization';
import CTASection from '../components/CTASection';

// Avantages de la logistique avec OuiGoo — le cœur de la promesse marchande.
const ADVANTAGES = [
  {
    icon: FaMotorcycle,
    title: 'Un réseau de livreurs à la demande',
    text: "Mobilisez des livreurs sur vos zones de couverture, sans gérer votre propre flotte.",
  },
  {
    icon: FaMoneyBillWave,
    title: 'Paiement à la livraison',
    text: "Vos clients règlent en espèces à la réception ; nous collectons le montant et vous le reversons.",
  },
  {
    icon: FaMapMarkedAlt,
    title: 'Suivi en temps réel',
    text: "Vous et vos clients suivez chaque colis, du retrait jusqu'à la livraison finale.",
  },
  {
    icon: FaBolt,
    title: 'Optimisation intelligente',
    text: "Regroupement des courses et itinéraires optimisés pour livrer plus vite et à moindre coût.",
  },
];

// Les canaux pour piloter sa logistique — présentés comme des avantages.
const CHANNELS = [
  {
    icon: FaMobileAlt,
    title: 'Application mobile',
    text: "Gérez commandes et livraisons depuis votre téléphone, où que vous soyez.",
    cta: "Découvrir l'application",
    href: '/ouigoo-app',
  },
  {
    icon: FaPuzzlePiece,
    title: 'Extensions e-commerce',
    text: "Connectez votre boutique Shopify ou WordPress en quelques minutes, sans code.",
    cta: 'Voir les intégrations',
    href: '#integrations',
  },
  {
    icon: FaCode,
    title: 'API & plateformes tierces',
    text: "Intégrez la livraison OuiGoo à votre application ou marketplace, sous votre marque.",
    cta: 'Voir la documentation API',
    href: 'https://ouigoo.gitbook.io/ouigoo/introduction/ouigoo-api',
    external: true,
  },
];

const MerchantsPage = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="font-sans">
      {/* Hero logistique */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-orange-50 via-white to-orange-50">
        <div className="container mx-auto text-center max-w-3xl">
          <h1 className="text-4xl lg:text-5xl font-extrabold text-gray-900 mb-5 leading-tight">
            Confiez votre <span className="text-primary-custom">logistique</span> à OuiGoo
          </h1>
          <p className="section-description mb-8">
            Réseau de livreurs, paiement à la livraison, suivi en temps réel et optimisation intelligente : concentrez-vous sur vos ventes, nous livrons.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="https://business.ouigoo.co" rel="noopener noreferrer" className="bg-primary-custom text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-custom-dark transition-colors">
              Créer mon compte marchand
            </a>
            <a href="/get-quote" className="border border-gray-300 text-gray-800 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
              Simuler un tarif
            </a>
          </div>
        </div>
      </section>

      {/* Avantages de la logistique avec OuiGoo */}
      <section className="py-16 bg-white">
        <div className="container mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-2 animate-on-scroll">
            La logistique, sans les tracas
          </h2>
          <p className="section-description animate-on-scroll">
            Tout ce qu'il faut pour livrer vos clients, sans investir dans votre propre chaîne logistique.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {ADVANTAGES.map(({ icon: Icon, title, text }) => (
              <div key={title} className="bg-white p-8 rounded-lg service-card text-left border border-gray-100">
                <div className="w-12 h-12 rounded-xl bg-orange-50 text-primary-custom flex items-center justify-center mb-6">
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="text-xl font-semibold mb-3 text-gray-800">{title}</h3>
                <p className="text-gray-600 font-inter">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vos canaux : app, extensions, API — présentés comme des avantages */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-2 animate-on-scroll">
            Pilotez votre logistique où vous voulez
          </h2>
          <p className="section-description animate-on-scroll">
            Application mobile, extensions e-commerce ou API : branchez la livraison OuiGoo là où vous travaillez.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {CHANNELS.map(({ icon: Icon, title, text, cta, href, external }) => (
              <a
                key={title}
                href={href}
                {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                className="group flex flex-col bg-white p-8 rounded-lg service-card text-left border border-gray-100 hover:border-primary-custom/30"
              >
                <div className="w-12 h-12 rounded-xl bg-orange-50 text-primary-custom flex items-center justify-center mb-6">
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="text-xl font-semibold mb-3 text-gray-800">{title}</h3>
                <p className="text-gray-600 font-inter flex-1">{text}</p>
                <span className="mt-5 inline-flex items-center gap-2 font-semibold text-primary-custom group-hover:gap-3 transition-all">
                  {cta} <FaArrowRight className="w-3.5 h-3.5" />
                </span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Optimisation intelligente (détail) */}
      <AIOptimization />

      {/* Intégrations (logos + doc) */}
      <Integrations />

      {/* CTA final marchand */}
      <CTASection />
    </div>
  );
};

export default MerchantsPage;
